import React from 'react';

import { CiCircleAlert } from 'react-icons/ci';

import { getCurrencyDisplayName } from '@/utils/currency';

import { userUIStore } from '@/stores/userUIStore';

import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';

interface AcceptModalProps {
  url: string;
  originalCurrency?: string;
  fallbackCurrency?: string;
  isUSDOnly?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When set, Accept runs this instead of full-page navigation (e.g. embed Hub88 URL in iframe). */
  onAccept?: (url: string) => void | Promise<void>;
}

const AcceptModal = ({
  url,
  originalCurrency,
  fallbackCurrency = 'USD',
  isUSDOnly,
  isOpen,
  onOpenChange,
  onAccept,
}: AcceptModalProps) => {
  const { isAcceptModalOpen, toggleAcceptModal } = userUIStore();
  const isModalOpen = isOpen ?? isAcceptModalOpen;
  const handleOpenChange = onOpenChange || (() => { return toggleAcceptModal(); });

  // Determine message based on props
  const getMessage = () => {
    if (isUSDOnly) {
      return (
        <>
          Este juego es solo en dólares (USD)
          <br />
          <span className="font-bold">Verifica la conversión en tu país</span>
        </>
      );
    }

    if (originalCurrency && fallbackCurrency && originalCurrency !== fallbackCurrency) {
      const originalCurrencyName = getCurrencyDisplayName(originalCurrency);
      const fallbackCurrencyName = getCurrencyDisplayName(fallbackCurrency);
      return (
        <>
          Este juego no está disponible en {originalCurrencyName}
          <br />
          <span className="font-bold">¿Deseas continuar con {fallbackCurrencyName}?</span>
        </>
      );
    }

    // Default message (backward compatibility)
    return (
      <>
        Este juego es solo en dólares (USD)
        <br />
        <span className="font-bold">Verifica la conversión en tu país</span>
      </>
    );
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent className="max-w-lg mx-auto border-sivarbet-border bg-sivarbet-background text-sivarbet-text rounded-lg shadow-2xl shadow-black ">
        <DialogHeader className="flex flex-col items-center justify-center w-full">
          <CiCircleAlert className="text-red-500 text-4xl" />
          <DialogTitle className="text-center text-xl font-bold">Importante!</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-center text-wrap">
            {getMessage()}
          </p>
          <div className="flex justify-center gap-3">
            <Button
              onClick={() => {
                void (async () => {
                  try {
                    if (onAccept) {
                      await onAccept(url);
                    } else {
                      window.location.replace(url);
                    }
                  } finally {
                    handleOpenChange(false);
                  }
                })();
              }}
              className="bg-sivarbet-secondary hover:bg-sivarbet-secondary/90 text-sivarbet-text"
            >
              Aceptar
            </Button>
            <Button
              onClick={() => { handleOpenChange(false); }}
              className="bg-red-500 hover:bg-red-500/90 text-sivarbet-text"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptModal;
